import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { ChevronLeft, Users, Settings, Trash2, KeyRound } from 'lucide-react';
import { auth } from '../../config/firebase';
import { db } from '../../config/firebase';
import { doc, getDoc, updateDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import GroupSettings from './GroupSettings';
import InviteModal from './InviteModal';
import ConfirmDialog from '../ui/ConfirmDialog';
import { createNotification } from '../../services/notificationService';


function GroupView() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCreator, setIsCreator] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const groupRef = doc(db, 'groups', groupId);

    const unsubscribe = onSnapshot(groupRef, 
      async (doc) => {
        if (doc.exists()) {
          const groupData = { id: doc.id, ...doc.data() };
          setGroup(groupData);
          setIsCreator(groupData.createdBy === auth.currentUser?.uid);
          
          // Update current user's info if missing or outdated
          const currentUser = auth.currentUser;
          if (currentUser && groupData.members && groupData.members[currentUser.uid]) {
            // Check if display name is missing or needs updating
            if (!groupData.members[currentUser.uid].displayName || 
                (currentUser.displayName && 
                 groupData.members[currentUser.uid].displayName !== currentUser.displayName)) {
              
              console.log('Updating user profile in group');
              try {
                await updateDoc(groupRef, {
                  [`members.${currentUser.uid}.displayName`]: currentUser.displayName || 'User'
                });
              } catch (err) {
                console.error('Error updating user profile:', err);
              }
            }
          }
          
          setLoading(false);
        } else {
          setError('Group not found');
          setLoading(false);
        }
      },
      (error) => {
        console.error("Error fetching group:", error);
        setError('Error loading group: ' + error.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [groupId]);

  // In the handleLeaveGroup function in GroupView.js:
  const handleLeaveGroup = async () => {
      setIsLeaveDialogOpen(true);
    };

    const confirmLeaveGroup = async () => {
    try {
      const userId = auth.currentUser?.uid;
      const userName = auth.currentUser?.displayName || 'A user';
      if (!userId) return;

      const updatedMembers = { ...group.members };
      delete updatedMembers[userId];

      await updateDoc(doc(db, 'groups', groupId), {
        members: updatedMembers
      });

      // Notify the user who left
      await createNotification(
        userId,
        'membership',
        'left',
        `You left the group: ${group.name}`,
        {
          relatedGroupId: groupId,
          relatedGroupName: group.name
        }
      );
      
      // Notify the group creator if they exist and are not the one leaving
      if (group.createdBy && group.createdBy !== userId) {
        await createNotification(
          group.createdBy,
          'membership',
          'left',
          `${userName} left your group: ${group.name}`,
          {
            relatedGroupId: groupId,
            relatedGroupName: group.name,
            relatedUserId: userId,
            relatedUserName: userName
          }
        );
      }

          setIsLeaveDialogOpen(false);
          navigate('/dashboard');
        } catch (error) {
          setError('Error leaving group: ' + error.message);
          setIsLeaveDialogOpen(false);
        }
      };

  const handleDeleteGroup = async () => {
    setIsDeleteDialogOpen(true);
  };

  // New function to handle confirmed delete action
    const confirmDeleteGroup = async () => {
      try {
      // Before deleting the group, get the member IDs to notify them
      const memberIds = Object.keys(group.members || {});
      const groupName = group.name;
      const creatorId = auth.currentUser?.uid;
      
      await deleteDoc(doc(db, 'groups', groupId));
      
      // Notify the creator
      await createNotification(
        creatorId,
        'group',
        'deleted',
        `You deleted the group: ${groupName}`,
        {
          relatedGroupName: groupName
        }
      );
      
      // Notify all other members
      for (const memberId of memberIds) {
        if (memberId !== creatorId) {
          await createNotification(
            memberId,
            'group',
            'deleted',
            `The group "${groupName}" has been deleted by the admin`,
            {
              relatedGroupName: groupName,
              relatedUserId: creatorId
            }
          );
        }
      }
      
      setIsDeleteDialogOpen(false);
      navigate('/dashboard');
    } catch (error) {
      setError('Error deleting group: ' + error.message);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleStatusChange = async (userId, checked) => {
    try {
      const docRef = doc(db, 'groups', groupId);
      await updateDoc(docRef, {
        [`members.${userId}.status`]: checked,
        [`members.${userId}.lastUpdated`]: new Date()
      });
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Error updating status: ' + error.message);
    }
  };

  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto pt-10">
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto pt-10">
          <div className="text-red-600">{error}</div>
          <Button 
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="mt-4"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (!group) return null;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto pt-10">
        <Button 
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6 text-gray-600 hover:text-gray-900 -ml-2 flex items-center"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back to Dashboard
        </Button>

        <Card>
          <CardHeader className="pb-2">
            {/* First row: Settings buttons and title */}
            <div className="flex justify-end mb-2">
              <div className="flex items-center space-x-3">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setIsSettingsOpen(true)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <Settings className="h-5 w-5" />
                </Button>
                {isCreator && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={handleDeleteGroup}
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                )}
              </div>
            </div>
            
            {/* Second row: Centered title */}
            <div className="text-center -mt-2">
              <CardTitle className="text-2xl font-bold">
                {group.name}
              </CardTitle>
              {group.description && (
                <p className="text-gray-600 mt-3">{group.description}</p>
              )}
            </div>
          </CardHeader>

          <CardContent>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-3 text-center">
                  Members ({Object.keys(group.members || {}).length})
                </h3>
                <div className="space-y-2">
                  {Object.entries(group.members || {}).map(([userId, memberData]) => {
                    // Log member data for debugging
                    console.log(`Member ${userId}:`, memberData);
                    
                    return (
                    <div key={userId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                          <span className="text-sm font-medium text-white">
                            {getInitials(memberData.displayName || 'User')}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">{memberData.displayName || 'User'}</span>
                          {userId === group.createdBy && (
                            <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full flex items-center">
                              <KeyRound className="mr-1 h-3 w-3" />
                              Admin
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className={memberData.status ? 'text-green-600' : 'text-gray-500'}>
                          {memberData.status ? 'Available' : 'Unavailable'}
                        </span>
                        {userId === auth.currentUser?.uid && (
                          <Switch 
                            checked={memberData.status || false}
                            onCheckedChange={(checked) => handleStatusChange(userId, checked)}
                            className="data-[state=checked]:bg-green-500"
                          />
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>

              <div className="border-t pt-6">
                <div className="space-y-3">
                  {group.createdBy === auth.currentUser?.uid && (
                    <Button 
                      variant="outline"
                      className="w-full flex items-center justify-center space-x-2 text-gray-600"
                      onClick={() => setIsInviteModalOpen(true)}
                    >
                      <Users className="h-4 w-4" />
                      <span>Invite Members</span>
                    </Button>
                  )}
                  
                  {/* Add this Leave Group button - only show if user is not the creator */}
                  {group.createdBy !== auth.currentUser?.uid && (
                      <Button 
                        variant="outline"
                        className="w-full flex items-center justify-center space-x-2 text-red-600 border border-red-200 hover:bg-red-50 hover:text-red-700 rounded-xl py-3 font-medium"
                        onClick={handleLeaveGroup}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                          <polyline points="16 17 21 12 16 7"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12"></line>
                        </svg>
                        <span>Leave Group</span>
                      </Button>
                    )}
                  </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Settings Modal */}
        {group && (
          <GroupSettings 
            group={group}
            isOpen={isSettingsOpen}
            onClose={() => setIsSettingsOpen(false)}
            onUpdate={() => {}} // The real-time listener will handle updates
          />
        )}

        {/* Invite Modal */}
        {group && (
          <InviteModal 
            group={group}
            isOpen={isInviteModalOpen}
            onClose={() => setIsInviteModalOpen(false)}
          />
        )}

        <ConfirmDialog 
          isOpen={isDeleteDialogOpen}
          title="Delete Group"
          message="Are you sure you want to delete this group? This action cannot be undone."
          onConfirm={confirmDeleteGroup}
          onCancel={() => setIsDeleteDialogOpen(false)}
        />
        
        <ConfirmDialog 
          isOpen={isLeaveDialogOpen}
          title="Leave Group"
          message="Are you sure you want to leave this group?"
          onConfirm={confirmLeaveGroup}
          onCancel={() => setIsLeaveDialogOpen(false)}
        />

      </div>
    </div>
  );
}

export default GroupView;